<template>
  <div class="flex flex-col gap-6">
    <template v-if="isLoadingEvaluation">
      <div class="h-[180px] bg-gray-100 rounded-xl animate-pulse" />
      <div class="h-[420px] bg-gray-100 rounded-xl animate-pulse" />
    </template>

    <template v-else-if="evaluation">
      <HandoverFlowStrip
        :requester="evaluation.package.requester"
        :candidate="evaluation.currentCandidate"
        :message="evaluation.message"
        :total-invites="evaluation.package.invites.length"
        :is-expired="isExpired"
        :is-processing="isProcessing"
        @approve="handleApprove"
        @decline="handleDecline"
        @viewAllCandidates="handleViewAllCandidates"
      />

      <PackageDetailCard :package-data="evaluation.package" />
    </template>

    <div v-else class="flex items-center justify-center py-16 text-sm text-[#7f7f8e]">
      Nenhuma avaliação encontrada
    </div>

    <HandoverConfirmationModal
      v-model="showApproveModal"
      variant="approve"
      :title="'Aprovar troca de plantões?'"
      :message="approveMessage"
      :loading="isProcessing"
      :schedule-conflicts="scheduleConflicts"
      :consecutive-hours-violations="consecutiveHoursViolations"
      @confirm="confirmApprove"
    />

    <HandoverConfirmationModal
      v-model="showDeclineModal"
      variant="decline"
      :title="'Reprovar candidato?'"
      :message="declineMessage"
      :loading="isProcessing"
      @confirm="confirmDecline"
    />

    <KillSwitchConfirmModal
      v-if="evaluation && evaluation.currentCandidate && killSwitchImpact"
      v-model="showKillSwitchModal"
      :package-data="evaluation.package"
      :candidate="evaluation.currentCandidate"
      :impact="killSwitchImpact"
      :loading="isProcessing"
      :schedule-conflicts="scheduleConflicts"
      @confirm="confirmKillSwitchApprove"
    />

    <CandidatesModal
      v-if="evaluation"
      v-model="showCandidatesModal"
      :invites="evaluation.package.invites"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useHandoverStore } from '@/pinia-store/useHandoverStore'
import { getFirstAndSecondName } from '@/utils/getFirstAndSecondName'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useNotificationStore } from '@/pinia-store/useNotificationStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { PackageStatus } from '@/Network/Types/Requests/HandoverInviteRequests'
import { ScheduleConflictsSummary } from '@/Network/Types/Responses/HandoverInviteResponses'
import type { ConsecutiveHoursViolation } from '@/Network/Types/Responses/ConsecutiveHoursResponses'
import HandoverFlowStrip from './components/HandoverFlowStrip.vue'
import PackageDetailCard from './components/PackageDetailCard.vue'
import HandoverConfirmationModal from './components/HandoverConfirmationModal.vue'
import KillSwitchConfirmModal from './components/KillSwitchConfirmModal.vue'
import CandidatesModal from './components/CandidatesModal.vue'
import {
  approvalsQueueBreadcrumbs,
  approvalsQueueLocation
} from '@/modules/company/Approvals/Hub/queues'

const route = useRoute()
const router = useRouter()
const handoverStore = useHandoverStore()
const headerStore = useHeaderStore()
const notificationStore = useNotificationStore()
const { flags } = useFeatureFlags()

const {
  currentEvaluation: evaluation,
  isLoadingEvaluation,
  isProcessing
} = storeToRefs(handoverStore)

const packageId = computed(() => Number(route.params.packageId))

const showApproveModal = ref(false)
const showDeclineModal = ref(false)
const showCandidatesModal = ref(false)
const showKillSwitchModal = ref(false)

const killSwitchImpact = ref<{
  hasConflicts: boolean
  originDoctor: { id: number; name: string; crm: string; avatarUrl: string | null }
  affectedShiftsCount: number
  affectedShiftsDescription: string
  activeAcceptanceCount: number
  candidatesCount: number
} | null>(null)

const scheduleConflicts = ref<ScheduleConflictsSummary | null>(null)
const consecutiveHoursViolations = ref<ConsecutiveHoursViolation[]>([])

const approveMessage = computed(() => {
  if (!evaluation.value) return ''
  const stationCount = evaluation.value.package.stations.length
  const requester = getFirstAndSecondName(evaluation.value.package.requester.name)
  const candidate = getFirstAndSecondName(evaluation.value.currentCandidate?.doctor.name ?? '')
  return `Tem certeza que deseja aprovar a troca de ${stationCount} plantões do Dr(a). ${requester} para o Dr(a). ${candidate}? Essa ação não poderá ser desfeita`
})

const isLastCandidate = computed(() => {
  return evaluation.value?.totalInQueue === 1
})

const isExpired = computed(() => {
  if (!evaluation.value) return false
  return new Date(evaluation.value.package.expiresAt).getTime() < Date.now()
})

const declineMessage = computed(() => {
  if (!evaluation.value) return ''
  const candidate = getFirstAndSecondName(evaluation.value.currentCandidate?.doctor.name ?? '')
  if (isLastCandidate.value) {
    return `Tem certeza que deseja reprovar o(a) Dr(a). ${candidate}? Esse é o último candidato e caso reprovado, a troca será cancelada por completo`
  }
  return `Tem certeza que deseja reprovar o(a) Dr(a). ${candidate}? ${evaluation.value.message}`
})

const goBack = () => {
  router.push(approvalsQueueLocation('handoverApprovals'))
}

const markRelatedNotificationAsRead = async () => {
  const targetUrl = `/aprovacoes/trocas/${packageId.value}/analisar`
  const notification = notificationStore.notifications.find((n) => n.targetUrl === targetUrl)
  if (notification && !notification.isRead) {
    await notificationStore.markAsRead(notification.id)
  }
}

const handleViewAllCandidates = () => {
  showCandidatesModal.value = true
}

const handleApprove = async () => {
  if (!evaluation.value?.currentCandidate || isExpired.value) return
  const preview = await handoverStore.fetchApprovePreview(packageId.value)
  if (!preview) return

  scheduleConflicts.value = preview.scheduleConflicts
  consecutiveHoursViolations.value = flags.enabledConsecutiveHoursLimit
    ? (preview.consecutiveHoursViolations ?? [])
    : []

  // Bloqueio duro: com violação de jornada, abre o modal de aprovação apenas para
  // exibir o motivo — o botão de confirmar fica desabilitado. Não passa pelo kill switch.
  if (consecutiveHoursViolations.value.length > 0) {
    showApproveModal.value = true
    return
  }

  if (preview.hasConflicts && preview.conflictingSummary) {
    killSwitchImpact.value = {
      hasConflicts: true,
      originDoctor: {
        id: 0,
        name: preview.conflictingSummary.originDoctor.name,
        crm: preview.conflictingSummary.originDoctor.crm,
        avatarUrl: null
      },
      affectedShiftsCount: preview.conflictingSummary.affectedShiftsCount,
      affectedShiftsDescription: preview.conflictingSummary.affectedShiftsDescription,
      activeAcceptanceCount: preview.conflictingSummary.activeAcceptanceCount,
      candidatesCount: preview.conflictingSummary.candidatesCount
    }
    showKillSwitchModal.value = true
  } else {
    showApproveModal.value = true
  }
}

const handleDecline = () => {
  if (!evaluation.value?.currentCandidate || isExpired.value) return
  showDeclineModal.value = true
}

/**
 * O preview pode passar limpo e a escrita ser recusada depois (revalidação dentro da
 * transação). Quando a recusa traz violações de jornada, cai no mesmo bloqueio duro do
 * preview: modal de aprovação aberto só para mostrar o motivo, com o confirmar desabilitado.
 */
const applyApprovalBlock = () => {
  const violations = handoverStore.approvalConsecutiveHoursViolations
  if (violations.length === 0) return
  consecutiveHoursViolations.value = violations
  showKillSwitchModal.value = false
  showApproveModal.value = true
}

const confirmApprove = async () => {
  if (!evaluation.value) return
  const success = await handoverStore.adminApproveFromEvaluation(evaluation.value.package.id)
  if (!success) {
    applyApprovalBlock()
    return
  }
  showApproveModal.value = false
  await markRelatedNotificationAsRead()
  await handoverStore.fetchPendingApprovals()
  goBack()
}

const confirmKillSwitchApprove = async (reason: string) => {
  if (!evaluation.value) return
  const success = await handoverStore.adminApproveFromEvaluation(
    evaluation.value.package.id,
    reason
  )
  if (!success) {
    applyApprovalBlock()
    return
  }
  showKillSwitchModal.value = false
  await markRelatedNotificationAsRead()
  await handoverStore.fetchPendingApprovals()
  goBack()
}

const confirmDecline = async () => {
  if (!evaluation.value) return
  const result = await handoverStore.adminDeclineFromEvaluation(evaluation.value.package.id)
  showDeclineModal.value = false
  if (result === null) {
    await markRelatedNotificationAsRead()
    await handoverStore.fetchPendingApprovals()
    goBack()
  }
}

onMounted(async () => {
  headerStore.setTitle(
    'Avaliação de Troca',
    'Avalie e aprove solicitações de trocas e repasses de plantão',
    approvalsQueueBreadcrumbs('handoverApprovals')
  )

  await handoverStore.fetchAdminQueueItem(packageId.value)

  if (
    evaluation.value?.package.status &&
    evaluation.value.package.status !== PackageStatus.IN_ADMIN_QUEUE
  ) {
    await markRelatedNotificationAsRead()
    goBack()
  }
})

onUnmounted(() => {
  handoverStore.setCurrentEvaluation(null)
})
</script>
